import type { DashboardFilters } from '@myapp/contract'
import {
  MONTH_KEY_PATTERN,
  lastDayOfMonth,
  monthsBetween,
  toMonthKey,
} from '../../../entities/dashboard'
import {
  DASHBOARD_FIRST_YEAR,
  DASHBOARD_START_MONTH,
  DashboardFiltersForm,
} from './dashboardFilters.types'

export function currentMonthKey(now: Date): string {
  return toMonthKey(now.getFullYear(), now.getMonth() + 1)
}

/** Never earlier than the start of the system, so a default before 09/2026 still has a range. */
export function clampMonth(key: string): string {
  return key < DASHBOARD_START_MONTH ? DASHBOARD_START_MONTH : key
}

/** First day of the system up to the current month; the year view defaults to the current year. */
export function getDashboardDefaults(now: Date): Pick<DashboardFiltersForm, 'year' | 'from' | 'to'> {
  return {
    year: Math.max(DASHBOARD_FIRST_YEAR, now.getFullYear()),
    from: DASHBOARD_START_MONTH,
    to: clampMonth(currentMonthKey(now)),
  }
}

export function dashboardYearOptions(now: Date): number[] {
  const last = Math.max(DASHBOARD_FIRST_YEAR, now.getFullYear() + 1)
  const years: number[] = []
  for (let y = DASHBOARD_FIRST_YEAR; y <= last; y += 1) years.push(y)
  return years
}

/** Every month a Von/Bis select offers: from the system start to December of next year. */
export function dashboardMonthOptions(now: Date): string[] {
  return monthsBetween(DASHBOARD_START_MONTH, toMonthKey(now.getFullYear() + 1, 12))
}

/**
 * URL params are user-editable, so whatever arrives is pulled back into a valid range: a month
 * before the start of the system or a malformed one becomes the start, and a Bis before the Von
 * collapses onto it.
 */
export function sanitizeDashboardFilters(form: DashboardFiltersForm, now: Date): DashboardFiltersForm {
  const lastYear = dashboardYearOptions(now).slice(-1)[0]
  const validMonth = (key: string, fallback: string) =>
    MONTH_KEY_PATTERN.test(key) ? clampMonth(key) : fallback
  const from = validMonth(form.from, DASHBOARD_START_MONTH)
  const to = validMonth(form.to, from)
  return {
    ...form,
    year: Math.min(lastYear, Math.max(DASHBOARD_FIRST_YEAR, form.year)),
    from,
    to: to < from ? from : to,
  }
}

/** First and last month of the selected period, "YYYY-MM". */
export function periodBounds(form: DashboardFiltersForm): { first: string; last: string } {
  if (form.mode === 'year') {
    return {
      first: form.year === DASHBOARD_FIRST_YEAR ? DASHBOARD_START_MONTH : toMonthKey(form.year, 1),
      last: toMonthKey(form.year, 12),
    }
  }
  return { first: form.from, last: form.to }
}

export function periodMonths(form: DashboardFiltersForm): string[] {
  const { first, last } = periodBounds(form)
  return monthsBetween(first, last)
}

/** Only the period and the technician go to the API; Gewerk and status are cut in the browser. */
export function toDashboardApiParams(form: DashboardFiltersForm): DashboardFilters {
  const { first, last } = periodBounds(form)
  return {
    dateFrom: `${first}-01`,
    dateTo: lastDayOfMonth(last),
    ...(form.technicianId != null ? { technicianId: form.technicianId } : {}),
  }
}
